/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import {
    BaseQueryFn,
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from "@reduxjs/toolkit/dist/query/react";
import Cookies from "js-cookie";

import type { RootState } from "store";
import { ExtendedFetchBaseQueryError } from "common/types/ExtendedFetchBaseQueryError";
import { ID_TOKEN, REFRESH_TOKEN } from "pages/LoginWeb/constants";
import { refreshAppToken, refreshWebToken } from "store/api/utils/tokenUtils";

export const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, unknown> = async (
    args,
    api,
    extraOptions
) => {
    const baseQuery = fetchBaseQuery({
        baseUrl: "",
        prepareHeaders: (headers, { getState }) => {
            const webToken = Cookies.get(ID_TOKEN);
            const { authToken: mobileToken, tenantId } = (getState() as any).configuration;
            if (webToken || mobileToken) {
                headers.set("x-via-driver-auth", webToken || mobileToken);
            }
            if (tenantId) {
                headers.set("x-via-tenant-id", tenantId);
            }
            return headers;
        },
    });

    const baseUrl = (api.getState() as RootState).configuration.baseApiUrl;
    const adjustedArgs = adjustBaseQueryArgs(args, baseUrl);
    const startTime = Date.now();

    let baseResult = await baseQuery(adjustedArgs, api, extraOptions);
    if (baseResult.error) {
        const statusCode = baseResult.error.status === 'PARSING_ERROR' ? baseResult.error.originalStatus : baseResult.error.status;
        if (statusCode === 401 || statusCode === 403) {
            const {
                authConfiguration,
                authToken: appToken,
                isDriverApp,
            } = (api.getState() as RootState).configuration;

            // Refresh the app token if present
            if (appToken && isDriverApp) {
                const newAppToken = await refreshAppToken(api);
                if (newAppToken) {
                    baseResult = await baseQuery(adjustedArgs, api, extraOptions);
                }
            }

            // Refresh the web token if present
            const webToken = Cookies.get(REFRESH_TOKEN);
            if (webToken) {
                const tokenRefreshed = await refreshWebToken(authConfiguration, webToken);
                if (tokenRefreshed) {
                    baseResult = await baseQuery(adjustedArgs, api, extraOptions);
                }
            }
        }
    }

    if (baseResult.error) {
        baseResult.error = extendFetchBaseQueryError(baseResult, { errorMeta: { startTime } });
    }

    return baseResult;
};

// Adjust the query arguments with the base URL
export const adjustBaseQueryArgs = (
    args: string | FetchArgs,
    baseUrl: string
): string | FetchArgs => {
    const urlEnd = typeof args === "string" ? args : args.url;
    const adjustedUrl = `${baseUrl}/${urlEnd}`;
    return typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };
};

// Extend fetch base query error with additional metadata
export const extendFetchBaseQueryError = (
    result: {
        error: FetchBaseQueryError;
        meta?: FetchBaseQueryMeta;
    },
    { errorMeta }: Partial<Pick<ExtendedFetchBaseQueryError, "errorMeta">>
) => ({
    ...result.error,
    errorMeta: {
        data: result?.error?.data,
        requestUrl: result?.meta?.request?.url,
        endTime: Date.now(),
        status: result?.meta?.response?.status,
        statusText: result?.meta?.response?.statusText,
        ...errorMeta,
    },
});
